import React, {
  useState,
  useEffect,
  useContext,
  useCallback,
  useRef,
  forwardRef,
  useImperativeHandle,
} from "react";
import { AuthContext } from "../../../../context/AuthContext";
import { fetchWithAuth } from "../../../../utils/api";
import Loader from "../../../ui/Loader";
import Modal from "../../../ui/Modal";
import Table from "../../../ui/Table";
import {
  swalError,
  swalSuccess,
  swalConfirm,
  swalWarning,
} from "../../../../utils/swalAlert";
import { Reorder } from "framer-motion";

// Assume this component is for managing levels stored in the institute model

const serverUrl = `${process.env.REACT_APP_SERVER_URL}`;

// Draggable level item for reorder mode
const DraggableLevel = ({ level, index, onDragStart, onDragEnd }) => {
  return (
    <Reorder.Item
      value={level}
      className="reorder-item"
      initial={{ opacity: 0, y: 15 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, scale: 0.95 }}
      transition={{ 
        type: "spring", 
        stiffness: 400, 
        damping: 30, 
        delay: index * 0.03 
      }}
      whileDrag={{
        scale: 1.03,
        boxShadow: "0 10px 30px rgba(0,0,0,0.12)",
        backgroundColor: "var(--primary-50)",
        borderColor: "var(--primary-300)",
        zIndex: 50,
        cursor: "grabbing",
      }}
      dragTransition={{ bounceStiffness: 600, bounceDamping: 35 }}
      onDragStart={onDragStart}
      onDragEnd={onDragEnd}
      style={{ cursor: "grab" }}
    >
      <div className="reorder-drag-handle" aria-hidden>
        <i className="fa-solid fa-grip-vertical"></i>
      </div>
      <span className="reorder-index">{index + 1}</span>
      <span className="reorder-level-name">{level}</span>
    </Reorder.Item>
  );
};

const ManageLevels = forwardRef((props, ref) => {
  const { user } = useContext(AuthContext);
  const [instituteData, setInstituteData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Modal states
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isCreating, setIsCreating] = useState(false);
  const [isUpdating, setIsUpdating] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  // Form states
  const [newLevel, setNewLevel] = useState("");
  const [editLevel, setEditLevel] = useState({ index: null, value: "" });
  const [initialEditValue, setInitialEditValue] = useState("");
  const [isEditChanged, setIsEditChanged] = useState(false);

  // Reorder mode states
  const [isReorderMode, setIsReorderMode] = useState(false);
  const [reorderedLevels, setReorderedLevels] = useState([]);
  const [isSavingOrder, setIsSavingOrder] = useState(false);

  // Auto-scroll the PAGE when dragging near viewport edges
  const rafRef = useRef(null);
  const isDraggingRef = useRef(false);
  const pointerYRef = useRef(0);
  const moveListenerRef = useRef(null);

  const autoScrollLoop = useCallback(() => {
    if (!isDraggingRef.current) return;
    const y = pointerYRef.current;
    const vh = window.innerHeight;
    const threshold = 120;
    const maxSpeed = 25;

    let delta = 0;
    if (y < threshold) {
      // Near top of viewport
      const intensity = (threshold - y) / threshold;
      delta = -intensity * intensity * maxSpeed;
    } else if (y > vh - threshold) {
      // Near bottom of viewport
      const intensity = (y - (vh - threshold)) / threshold;
      delta = intensity * intensity * maxSpeed;
    }

    if (delta !== 0) {
      window.scrollBy(0, delta);
    }
    rafRef.current = requestAnimationFrame(autoScrollLoop);
  }, []);

  const handleItemDragStart = useCallback(() => {
    isDraggingRef.current = true;
    document.body.style.userSelect = "none";
    const onMove = (e) => {
      pointerYRef.current = e.clientY ?? (e.touches?.[0]?.clientY ?? 0);
    };
    window.addEventListener("pointermove", onMove);
    window.addEventListener("touchmove", onMove, { passive: true });
    moveListenerRef.current = onMove;
    rafRef.current = requestAnimationFrame(autoScrollLoop);
  }, [autoScrollLoop]);

  const handleItemDragEnd = useCallback(() => {
    isDraggingRef.current = false;
    document.body.style.userSelect = "";
    if (rafRef.current) cancelAnimationFrame(rafRef.current);
    if (moveListenerRef.current) {
      window.removeEventListener("pointermove", moveListenerRef.current);
      window.removeEventListener("touchmove", moveListenerRef.current);
      moveListenerRef.current = null;
    }
  }, []);

  useEffect(() => {
    return () => {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
      if (moveListenerRef.current) {
        window.removeEventListener("pointermove", moveListenerRef.current);
        window.removeEventListener("touchmove", moveListenerRef.current);
      }
    };
  }, []);

  useImperativeHandle(ref, () => ({
    openModal: () => setIsModalOpen(true),
  }));

  useEffect(() => {
    const fetchInstitute = async () => {
      setLoading(true);
      try {
        const response = await fetchWithAuth(
          `${serverUrl}/api/institute/get/${user.instituteId}`,
        );
        if (!response) return;
        if (!response.ok) throw new Error("Failed to fetch institute data");
        const data = await response.json();
        setInstituteData(data);
        setError(null);
      } catch (err) {
        setError(err.message);
        swalError("Failed to load institute data.");
      } finally {
        setLoading(false);
      }
    };
    fetchInstitute();
  }, [user.instituteId]);

  // Extract levels from institute data
  const levels = instituteData?.levels || [];

  const updateLevelsOnServer = async (updatedLevels, successMsg) => {
    if (!instituteData) return;
    setIsUpdating(true);
    try {
      const form = new FormData();
      // Only append levels as JSON string
      form.append("levels", JSON.stringify(updatedLevels));
      const response = await fetchWithAuth(
        `${serverUrl}/api/institute/update/${user.instituteId}`,
        {
          method: "PUT",
          body: form,
        },
      );
      if (!response) return;
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || "Failed to update levels");
      }
      const data = await response.json();
      setInstituteData(data);
      swalSuccess(successMsg);
      // Reset states on success
      setIsEditModalOpen(false);
      setIsModalOpen(false);
      setEditLevel({ index: null, value: "" });
      setInitialEditValue("");
      setNewLevel("");
      setIsEditChanged(false);
    } catch (err) {
      swalError("Failed to update levels.");
    } finally {
      setIsUpdating(false);
      setIsCreating(false);
      setIsDeleting(false);
    }
  };

  // Add (supports comma separated)
  const handleCreateLevel = async (e) => {
    e.preventDefault();
    if (!newLevel.trim()) {
      swalError("Please provide the level value.");
      return;
    }
    setIsCreating(true);
    const newItems = newLevel
      .split(",")
      .map((v) => v.trim())
      .filter((v) => v.length > 0 && !levels.includes(v)); // Filter out empty and duplicates

    if (newItems.length === 0) {
      swalError(`Level already exist`);
      setIsCreating(false);
      return;
    }
    const updatedLevels = [...levels, ...newItems];
    await updateLevelsOnServer(
      updatedLevels,
      newItems.length === 1
        ? "Level created successfully."
        : "Levels created successfully.",
    );
  };

  // Edit
  const handleEditLevel = async (e) => {
    e.preventDefault();
    if (!editLevel.value.trim()) {
      swalError("Please provide the level value.");
      return;
    }
    setIsUpdating(true);
    const updatedLevels = levels.map((lvl, i) =>
      i === editLevel.index ? editLevel.value.trim() : lvl,
    );
    await updateLevelsOnServer(updatedLevels, "Level updated successfully.");
  };

  // Delete
  const handleDelete = async (index) => {
    const result = await swalConfirm(
      "Are you sure?",
      "You won't be able to revert this!",
      "Yes, delete it!",
      "Cancel",
    );
    if (result.isConfirmed) {
      setIsDeleting(true);
      const updatedLevels = levels.filter((_, i) => i !== index);
      await updateLevelsOnServer(updatedLevels, "Level deleted successfully.");
    }
  };

  // Reorder handlers
  const handleEnterReorder = () => {
    if (levels.length < 2) {
      swalWarning("Need at least 2 levels to reorder.");
      return;
    }
    setReorderedLevels([...levels]);
    setIsReorderMode(true);
  };

  const handleCancelReorder = () => {
    setIsReorderMode(false);
    setReorderedLevels([]);
  };

  const handleSaveOrder = async () => {
    const orderChanged = reorderedLevels.some((lvl, i) => lvl !== levels[i]);
    if (!orderChanged) {
      swalWarning("No changes detected in the order.");
      return;
    }
    setIsSavingOrder(true);
    await updateLevelsOnServer(
      reorderedLevels,
      "Levels reordered successfully.",
    );
    setIsReorderMode(false);
    setReorderedLevels([]);
    setIsSavingOrder(false);
  };

  // Table columns
  const columns = [
    { key: "srNo", label: "Sr. No.", sortable: true },
    { key: "value", label: "Level", sortable: true },
    { key: "edit", label: "", sortable: false },
    { key: "delete", label: "", sortable: false },
  ];

  // Table data
  const enhancedLevels = levels.map((lvl, i) => ({
    srNo: i + 1,
    value: lvl,
    edit: (
      <button
        className="edit-btn"
        onClick={() => {
          setEditLevel({ index: i, value: lvl });
          setInitialEditValue(lvl);
          setIsEditModalOpen(true);
          setIsEditChanged(false);
        }}
        disabled={isDeleting || isUpdating || isCreating}
      >
        <i className="fa-solid fa-pen-to-square"></i>
      </button>
    ),
    delete: (
      <button
        className="delete-btn"
        onClick={() => handleDelete(i)}
        disabled={isDeleting || isUpdating || isCreating}
      >
        <i className="fa-solid fa-trash-can"></i>
      </button>
    ),
  }));

  return (
    <>
      <div className="manage-container">
        {/* Hide header entirely during reorder mode */}
        {!isReorderMode && (
          <div className="header">
            {!loading && !error && levels.length >= 2 && (
              <button
                className="create-btn"
                onClick={handleEnterReorder}
                disabled={isDeleting || isUpdating || isCreating}
              >
                <i className="fa-solid fa-arrows-up-down"></i> Reorder
              </button>
            )}
            <button
              className="create-btn"
              onClick={() => setIsModalOpen(true)}
            >
              Add Level +
            </button>
          </div>
        )}
        {/* Main Content Area */}
        <div className="manage-content">
          {loading && <Loader fontSize="2rem" />}
          {error && <div className="error-message">{error}</div>}

          {/* Normal table view */}
          {!loading && !error && !isReorderMode && (
            <Table data={enhancedLevels} columns={columns} caption="Levels" />
          )}

          {/* Reorder mode — uses Table-consistent wrapper */}
          {!loading && !error && isReorderMode && (
            <div className="table-wrapper glass">
                {/* Caption bar matching the Table caption */}
                <div className="reorder-caption">
                  <span>
                    <i className="fa-solid fa-arrows-up-down"></i> Reorder
                    Levels
                  </span>
                  <div className="reorder-header-actions">
                    <button
                      className="reorder-cancel-btn"
                      onClick={handleCancelReorder}
                      disabled={isSavingOrder}
                    >
                      <i className="fa-solid fa-xmark"></i> Cancel
                    </button>
                    {isSavingOrder ? (
                      <Loader />
                    ) : (
                      <button
                        className="reorder-save-btn"
                        onClick={handleSaveOrder}
                      >
                        <i className="fa-solid fa-check"></i> Save Order
                      </button>
                    )}
                  </div>
                </div>

                <Reorder.Group
                  axis="y"
                  values={reorderedLevels}
                  onReorder={setReorderedLevels}
                  className="reorder-list"
                >
                  {reorderedLevels.map((level, index) => (
                    <DraggableLevel
                      key={level}
                      level={level}
                      index={index}
                      onDragStart={handleItemDragStart}
                      onDragEnd={handleItemDragEnd}
                    />
                  ))}
                </Reorder.Group>
                <div className="reorder-hint">
                  <i className="fa-solid fa-hand-pointer"></i> Drag to reorder
                  levels
                </div>
              </div>
            )}
        </div>
        {/* Create Modal */}
        <Modal
          isOpen={isModalOpen}
          onClose={() => !isCreating && setIsModalOpen(false)}
        >
          <h2>Add New Level</h2>
          <form onSubmit={handleCreateLevel} className="department-form">
            <div className="form-group">
              <label>
                <i className="fa-solid fa-asterisk requiredIcon"></i> Level
              </label>
              <input
                type="text"
                name="level"
                value={newLevel}
                placeholder="e.g. Grade 8, Grade 9"
                onChange={(e) => setNewLevel(e.target.value)}
                required
                disabled={isCreating}
              />
            </div>
            {isCreating ? (
              <Loader />
            ) : (
              <button type="submit" className="btn-submit">
                Create Level
              </button>
            )}
          </form>
        </Modal>
        {/* Edit Modal */}
        <Modal
          isOpen={isEditModalOpen}
          onClose={() => !isUpdating && setIsEditModalOpen(false)}
        >
          <h2>Edit Level</h2>
          <form onSubmit={handleEditLevel} className="department-form">
            <div className="form-group">
              <label>
                <i className="fa-solid fa-asterisk requiredIcon"></i> Level
              </label>
              <input
                type="text"
                name="level"
                value={editLevel.value}
                placeholder="e.g. Grade 8, Grade 9"
                onChange={(e) => {
                  setEditLevel({ ...editLevel, value: e.target.value });
                  setIsEditChanged(e.target.value !== initialEditValue);
                }}
                required
                disabled={isUpdating}
              />
            </div>
            {isUpdating ? (
              <Loader />
            ) : (
              <button
                type="submit"
                className="btn-submit"
                disabled={!isEditChanged || isUpdating}
                onClick={(e) => {
                  if (!isEditChanged && !isUpdating) {
                    e.preventDefault();
                    swalWarning("No changes detected.");
                  }
                }}
              >
                Update Level
              </button>
            )}
          </form>
        </Modal>
      </div>
    </>
  );
});

export default ManageLevels;
